<svelte:options runes={false} />
<script>
  // SymbolUniverse (TASK-20260823-pairs-universe-ui) — renders ANY pair list as chips collapsed to
  // exactly ONE row of the space it is given (as many as fit, then a "+N more" pill) and expands
  // INLINE — no modal, no navigation — into the full chip grid with a filter, a count and a copy
  // action. A ≤10-pair strategy renders as one plain row with no affordance; a 400-name self-universe
  // panel renders as a row plus "+372 more". All decisions over data live in lib/symbolUniverse.js
  // (unit-tested); this file measures chip widths ONCE (off-flow, so measuring never disturbs the
  // host's layout) and re-fits arithmetically on every resize / list change.
  //
  // Selectable mode turns the chips into tabs (the Candles & Signals symbol switcher): while collapsed
  // the ACTIVE symbol is pinned first so it survives the collapse; arrows move focus (manual
  // activation — selecting on arrow would fire a series fetch per keystroke across 400 names);
  // Enter/Space/click select. `select` carries the RAW token the host sent — the series fetch keys on it.
  import { createEventDispatcher, onMount, onDestroy, tick } from 'svelte';
  import {
    parseSymbols, layoutRow, fitToRow, filterSymbols, summarizeSymbols, pinFirst, normalizePair, PROBE_SEED,
  } from '../../lib/symbolUniverse.js';
  import { nextRovingIndex } from '../../lib/rovingIndex.js';

  export let symbols = '';          // the hosts' comma-joined `symbols` field (request form)
  export let variant = 'inline';    // 'inline' — header / list rows · 'block' — panels
  export let dense = false;         // tighter chips (list rows); an expanded dense grid scrolls inside itself
  export let expandable = true;     // false when hosted INSIDE another interactive element (a row <button>):
                                    // nested controls are invalid HTML, so the pill becomes a static "+N" badge
  export let selectable = false;    // chips are tabs; dispatches `select` with the raw token
  export let selected = null;       // the active symbol (any delimiter form) in selectable mode
  export let label = 'Pairs';       // accessible name for the group / tablist
  export let emptyText = '';        // rendered (muted) when there are no pairs at all; '' renders nothing

  const dispatch = createEventDispatcher();

  let expanded = false;
  let query = '';
  // A NEW list (the operator switched strategies) always starts collapsed with no filter — an expanded
  // 400-name grid with a stale "btc" filter must not greet the next strategy.
  $: resetOnListChange(symbols);
  function resetOnListChange(_) { expanded = false; query = ''; }

  $: all = parseSymbols(symbols);
  // Pin only while collapsed: in the expanded grid the natural order stays put, so a selection does
  // not reshuffle 400 chips under the pointer.
  $: ordered = selectable && !expanded ? pinFirst(all, selected) : all;
  $: total = ordered.length;
  $: selectedKey = normalizePair(selected);
  $: summary = summarizeSymbols(ordered, 4);

  // ---- Collapsed-row fit -----------------------------------------------------------------------
  // Chip widths are invariant (nowrap, fixed font), so they are measured ONCE per display name in an
  // off-flow strip (`.su-measure`: absolute + hidden — it cannot widen the host or squeeze a flex
  // sibling). Every later fit — resize, list change, collapse — is pure arithmetic over the cache.
  let rootEl;
  let rowEl;
  let pillEl;
  let filterEl;
  let probeLimit = PROBE_SEED;
  $: probe = ordered.slice(0, probeLimit);
  let visible = 0;               // chips on the collapsed row (0 until the first fit lands, before paint)
  $: hidden = Math.max(0, total - visible);
  $: shown = expanded ? filterSymbols(ordered, query) : probe.slice(0, visible);
  $: countText = shown.length === total ? `${total} pairs` : `${shown.length} of ${total}`;

  let measuring = false;         // the measurement strip is mounted
  let toMeasure = [];            // entries whose width is not cached yet
  const widths = new Map();      // display → chip width (px), per instance (metrics differ per variant)
  let pillWidth = 0;             // widest "+N more" seen — "+{total} more" bounds every later "+{hidden} more"
  let pillMeasuredFor = 0;
  let lastWidth = -1;
  let lastProbe = null;
  let raf = 0;
  let ro = null;

  onMount(() => {
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => scheduleFit());
      ro.observe(rootEl); // the root always exists, even while the list is empty or the row is remounted
    }
  });
  onDestroy(() => {
    ro?.disconnect();
    if (raf) cancelAnimationFrame(raf);
    clearTimeout(copiedTimer);
  });

  $: if (probe && rootEl) scheduleFit();

  function scheduleFit() {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; void fit(); });
  }

  async function fit() {
    if (!rootEl || expanded || total === 0) return;
    const width = rootEl.getBoundingClientRect().width;
    if (!(width > 0)) return; // display:none host (a closed <details>) — the observer fires again when it opens
    if (width === lastWidth && probe === lastProbe) return;

    const missing = probe.filter((e) => !widths.has(e.display));
    if (missing.length > 0 || total > pillMeasuredFor) {
      toMeasure = missing;
      measuring = true;
      await tick();
      const strip = rootEl.querySelector('.su-measure');
      let laidOut = !!strip;
      if (strip) {
        for (const el of strip.querySelectorAll('[data-measure]')) {
          const w = el.getBoundingClientRect().width;
          if (!(w > 0)) { laidOut = false; break; }
          widths.set(el.dataset.measure, w);
        }
        const pw = strip.querySelector('[data-measure-pill]')?.getBoundingClientRect().width ?? 0;
        if (pw > 0) { pillWidth = Math.max(pillWidth, pw); pillMeasuredFor = total; } else laidOut = false;
      }
      measuring = false;
      toMeasure = [];
      if (!laidOut) return; // not renderable yet — the next observer tick retries
    }

    if (!rowEl) return;
    const gap = parseFloat(getComputedStyle(rowEl).columnGap) || 0;
    // Half a pixel of slack: the browser wraps on fractional overflow the cache cannot see.
    const containerWidth = width - 0.5;
    const offsets = layoutRow(probe.map((e) => widths.get(e.display) ?? 0), { gap, containerWidth });
    const r = fitToRow({ offsets, total, containerWidth, pillWidth, gap });
    if (r.visible === probe.length && probe.length < total) {
      // The whole probe fit — this row is wider than the seed assumed. Grow and fit again
      // (the reactive `probe` recompute schedules it).
      probeLimit = Math.min(total, probeLimit * 2);
      return;
    }
    visible = r.visible;
    lastWidth = width;
    lastProbe = probe;
  }

  // ---- Expand / collapse -----------------------------------------------------------------------
  async function expand() {
    if (!expandable) return;
    expanded = true;
    await tick();
    filterEl?.focus();
  }

  async function collapse() {
    expanded = false;
    query = '';
    await tick();
    lastWidth = -1; // the width may have changed while expanded (the observer is ignored then)
    await fit();
    await tick();
    (pillEl ?? rowEl)?.focus(); // back to the trigger; the row itself when everything now fits (AC 3)
  }

  function onRootKeydown(e) {
    if (e.key === 'Escape' && expanded) {
      e.preventDefault();
      e.stopPropagation();
      void collapse();
    }
  }

  // ---- Copy list (expanded toolbar) ------------------------------------------------------------
  let copied = false;
  let copiedTimer;
  async function copyList() {
    try {
      await navigator.clipboard.writeText(all.map((e) => e.display).join(', '));
      copied = true;
      clearTimeout(copiedTimer);
      copiedTimer = setTimeout(() => (copied = false), 1500);
    } catch {
      // Clipboard unavailable (insecure context / permission) — a no-op is the honest outcome.
    }
  }

  // ---- Selectable mode (tabs) ------------------------------------------------------------------
  function isActive(entry) {
    return selectable && entry.display === selectedKey;
  }
  async function choose(entry) {
    if (!selectable || isActive(entry)) return;
    dispatch('select', entry.raw);
    // The host sets `selected` synchronously; while collapsed, pinFirst then moves the chosen chip to
    // the front and the keyed each re-inserts its node — which blurs it. Put focus back on it.
    await tick();
    rowEl?.querySelector('.su-chip-tab.active')?.focus();
  }
  // Roving focus across the VISIBLE chips (collapsed: the fitted row; expanded: the filtered grid).
  function onChipsKeydown(e) {
    if (!selectable) return;
    const chips = Array.from(rowEl.querySelectorAll('[data-chip]'));
    const idx = chips.indexOf(e.target);
    if (idx < 0) return;
    const next = nextRovingIndex(e.key, idx, chips.length);
    if (next === null) return;
    e.preventDefault();
    chips[next].focus();
  }
</script>

<!-- Wrapper elements are <span>s (phrasing content) so the non-expandable variant is valid inside a row <button>. -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<span
  bind:this={rootEl}
  class="su-root su-{variant}"
  class:dense
  class:expanded
  role={selectable || total === 0 ? undefined : 'group'}
  aria-label={selectable || total === 0 ? undefined : `${label}: ${summary}`}
  on:keydown={onRootKeydown}
>
  {#if total === 0}
    {#if emptyText}<span class="su-empty-note">{emptyText}</span>{/if}
  {:else}
    {#if expanded}
      <span class="su-toolbar">
        <span class="su-count" aria-live="polite">{countText}</span>
        <input
          bind:this={filterEl}
          bind:value={query}
          class="su-filter"
          type="search"
          placeholder="Filter pairs…"
          aria-label={`Filter ${label}`}
          autocomplete="off"
          spellcheck="false"
        />
        <button type="button" class="su-tool" on:click={copyList} aria-live="polite">{copied ? 'Copied' : 'Copy list'}</button>
        <button type="button" class="su-tool" on:click={collapse}>Show fewer</button>
      </span>
    {/if}

    <span class="su-row">
      <!-- The chips container is the tablist in selectable mode and holds ONLY chips; the pill is its sibling. -->
      <!-- svelte-ignore a11y_no_noninteractive_element_interactions a11y_no_noninteractive_tabindex -->
      <span
        bind:this={rowEl}
        class="su-chips"
        class:collapsed={!expanded}
        role={selectable ? 'tablist' : undefined}
        aria-label={selectable ? label : undefined}
        tabindex="-1"
        on:keydown={onChipsKeydown}
      >
        {#each shown as entry, i (entry.display)}
          {#if selectable}
            <button
              type="button"
              role="tab"
              data-chip
              class="su-chip su-chip-tab"
              class:active={isActive(entry)}
              aria-selected={isActive(entry)}
              tabindex={i === 0 ? 0 : -1}
              on:click={() => choose(entry)}
            >{entry.display}</button>
          {:else}
            <span data-chip class="su-chip">{entry.display}</span>
          {/if}
        {/each}
        {#if expanded && shown.length === 0}
          <span class="su-empty-note">No pairs match “{query}”</span>
        {/if}
      </span>

      {#if !expanded && hidden > 0}
        {#if expandable}
          <button bind:this={pillEl} type="button" class="su-pill" aria-label={`Show all ${total} pairs`} on:click={expand}>+{hidden} more</button>
        {:else}
          <span class="su-pill su-pill-static" aria-label={`${hidden} more pairs`}>+{hidden}</span>
        {/if}
      {/if}
    </span>

    {#if measuring}
      <!-- Off-flow measurement strip: absolute + hidden, so measuring never moves the host's layout. -->
      <span class="su-measure" aria-hidden="true">
        {#each toMeasure as entry (entry.display)}
          <span class="su-chip" data-measure={entry.display}>{entry.display}</span>
        {/each}
        <span class="su-pill" data-measure-pill>{expandable ? `+${total} more` : `+${total}`}</span>
      </span>
    {/if}
  {/if}
</span>

<style>
  /* Tokens only (design-system.md): the .theme-paper cool-blue variables flow through untouched. */
  .su-root {
    --su-chip-h: 1.35rem;
    --su-font: var(--font-size-xs);
    --su-gap: var(--spacing-xs);
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    min-width: 0;
  }
  .su-root:empty { display: none; } /* an empty universe contributes no phantom gap to the host */
  .su-block { --su-chip-h: 1.55rem; --su-font: var(--font-size-sm); }
  .su-root.dense { --su-chip-h: 1.15rem; }

  .su-row {
    display: flex;
    align-items: center;
    gap: var(--su-gap);
    min-width: 0;
  }
  .su-chips {
    flex: 1 1 0;                      /* expanded: the grid takes the full row */
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--su-gap);
    outline: none;
  }
  /* One row, hard-clipped. Content-sized so the pill hugs the last chip (the fit itself is computed from the
     ROOT width, so this never feeds back into the measurement). */
  .su-chips.collapsed {
    flex: 0 1 auto;
    max-width: 100%;
    overflow: hidden;
    min-height: var(--su-chip-h);
    max-height: var(--su-chip-h);
  }
  .su-row > .su-pill { flex-shrink: 0; }
  .expanded .su-chips { animation: su-reveal var(--transition-fast); }
  .dense.expanded .su-chips { max-height: calc(12 * var(--su-chip-h)); overflow: auto; }
  @keyframes su-reveal { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) { .expanded .su-chips { animation: none; } }

  .su-measure {
    position: absolute;
    left: 0;
    top: 0;
    display: flex;
    flex-wrap: nowrap;
    gap: var(--su-gap);
    width: max-content;
    visibility: hidden;
    pointer-events: none;
  }

  .su-chip,
  .su-pill {
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    height: var(--su-chip-h);
    padding: 0 var(--spacing-sm);
    border-radius: var(--radius-sm);
    font-family: var(--font-family-base);
    font-size: var(--su-font);
    font-weight: var(--font-weight-medium);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
    line-height: 1;
    white-space: nowrap;
  }
  .su-chip {
    color: var(--text-primary);
    background: var(--color-primary-alpha-10);
    border: 1px solid var(--color-primary-alpha-20);
  }

  /* Tabs (selectable mode) */
  .su-chip-tab {
    appearance: none;
    cursor: pointer;
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
    transition: color var(--transition-fast), border-color var(--transition-fast), background var(--transition-fast);
  }
  .su-chip-tab:hover { color: var(--text-primary); border-color: var(--border-hover); }
  .su-chip-tab.active {
    color: var(--text-primary);
    background: var(--color-primary-alpha-20);
    border-color: var(--color-primary);
    font-weight: var(--font-weight-semibold);
  }

  /* The "+N more" pill — the affordance that says "this is a big universe" at a glance. */
  .su-pill {
    color: var(--text-secondary);
    background: var(--bg-quaternary);
    border: 1px solid var(--border-secondary);
    font-weight: var(--font-weight-semibold);
  }
  button.su-pill { appearance: none; cursor: pointer; transition: color var(--transition-fast), border-color var(--transition-fast); }
  button.su-pill:hover { color: var(--text-primary); border-color: var(--color-primary); }

  .su-chip-tab:focus-visible,
  .su-pill:focus-visible,
  .su-tool:focus-visible,
  .su-filter:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }

  /* Expanded toolbar: count · filter · copy · show fewer */
  .su-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacing-sm);
  }
  .su-count {
    min-width: 5.5rem;
    font-size: var(--font-size-xs);
    color: var(--text-secondary);
    font-variant-numeric: tabular-nums;
  }
  .su-filter {
    flex: 0 1 14rem;
    min-width: 8rem;
    height: 1.7rem;
    padding: 0 var(--spacing-sm);
    color: var(--text-primary);
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
  }
  .su-filter::placeholder { color: var(--text-muted); }
  .su-filter:focus { outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 2px var(--color-primary-alpha-20); }
  .su-tool {
    appearance: none;
    cursor: pointer;
    height: 1.7rem;
    padding: 0 var(--spacing-md);
    color: var(--text-secondary);
    background: transparent;
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    white-space: nowrap;
  }
  .su-tool:hover { color: var(--text-primary); background: var(--bg-tertiary); }

  .su-empty-note { font-size: var(--su-font); color: var(--text-muted); }
</style>
